import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import polygonClipping, { type Geom, type MultiPolygon as ClippingMultiPolygon, type Polygon as ClippingPolygon } from 'polygon-clipping';
import type { LayerModel, OperationalAttributeField, OperationalSubarea } from '../core/types';
import type { ClassificationConfig } from '../presentation/config';
import { fieldValueForMasterEntity } from './attributes';
import { hasThematicValue, type ClassificationAssignments } from './classification';
import { principalValue } from './dualClassification';
import { entityViewEntities } from './entityView';
import { measureArea } from './measurement';
import { logicalParentGeometry, SUBAREA_REMAINDER_TOLERANCE_M2 } from './operationalSubareas';

/**
 * Áreas parciales as THEMATIC EXCEPTIONS over a Suerte classification (categorized, Maestro, Ver por = Suertes).
 * For every visible Suerte that has subareas (parentLevel = suerte, parentId = that Suerte) with a value in the
 * partial field, the ACTIVE Suerte geometry is split into non-overlapping pieces:
 * - SUBAREA pieces = subarea ∩ active Suerte (value of the partial field wins over the Suerte value);
 * - BASE REMNANT = active Suerte − union(those pieces) (value of the base field, or "Sin valor").
 * Subareas without a value neither cut nor override. Nothing is stored or modified: pieces are rebuilt from the
 * active model, the subareas and the fields, and memoized by those references.
 */
const { difference, intersection } = polygonClipping;
const NO_VALUE = 'Sin valor';
const display = (value: unknown): string => hasThematicValue(value) ? String(value) : NO_VALUE;

export interface MixedThematicPiece {
  /** Entity the piece belongs to (a Suerte; with a V1.9 scenario, the entity of the current «Ver por»). */
  readonly parentSuerteId: string;
  readonly source: 'base' | 'subarea' | 'scenario';
  /** Suerte id for the base remnant, subarea id for an exception, entity id for a scenario piece. */
  readonly sourceId: string;
  readonly category: string;
  readonly hasValue: boolean;
  readonly geometry: Polygon | MultiPolygon;
  readonly areaHa: number;
}

export interface MixedClassificationAssignments extends ClassificationAssignments {
  /** Only Suertes actually split by at least one exception. */
  readonly piecesBySuerteId: ReadonlyMap<string, readonly MixedThematicPiece[]>;
  /**
   * Fragments of the Suertes actually classified: DisplaySet ∩ ThemeTarget, exactly as in the normal classification.
   * An Área parcial never promotes its Suerte into the theme: a Suerte outside the target stays context/remainder.
   */
  readonly subsetFragmentIds: ReadonlySet<string>;
}

/** Project fields that can carry exceptions: text fields at level Área parcial. */
export const partialExceptionFields = (fields: readonly OperationalAttributeField[]): readonly OperationalAttributeField[] =>
  fields.filter(({ descriptor }) => descriptor.level === 'subarea' && descriptor.type === 'string');

/** Whether the base classification can take exceptions at all (independent from the checkbox). */
export const supportsPartialExceptions = (config: Pick<ClassificationConfig, 'layerId' | 'field' | 'mode'>, model: LayerModel | null, level: string): boolean =>
  Boolean(model && config.layerId === model.id && config.field && config.mode === 'categorized' && level === 'suerte');

/** The partial field in effect, or null when the mixed view is off or not applicable. */
export function activePartialExceptionField(config: ClassificationConfig, model: LayerModel | null, level: string, fields: readonly OperationalAttributeField[]): OperationalAttributeField | null {
  const exceptions = config.partialExceptions;
  if (!exceptions?.enabled || !exceptions.fieldId || !supportsPartialExceptions(config, model, level)) return null;
  return partialExceptionFields(fields).find(({ descriptor }) => descriptor.id === exceptions.fieldId) ?? null;
}

interface SuerteExceptionGeometry {
  readonly overrides: readonly { readonly subareaId: string; readonly category: string; readonly geometry: Polygon | MultiPolygon; readonly areaM2: number }[];
  readonly remnant: Polygon | MultiPolygon | null;
}

export const asClipping = (geometry: Polygon | MultiPolygon): ClippingPolygon | ClippingMultiPolygon => geometry.getCoordinates() as ClippingPolygon | ClippingMultiPolygon;
/** Keeps only parts above the residue tolerance; null when nothing remains. */
export function significant(coordinates: ClippingMultiPolygon): { readonly coordinates: ClippingMultiPolygon; readonly geometry: Polygon | MultiPolygon } | null {
  const parts = coordinates.filter((part) => measureArea(new Polygon(part)).areaM2 > SUBAREA_REMAINDER_TOLERANCE_M2);
  if (!parts.length) return null;
  return { coordinates: parts, geometry: parts.length === 1 ? new Polygon(parts[0]!) : new MultiPolygon(parts) };
}

const geometryCache = new WeakMap<LayerModel, WeakMap<readonly OperationalSubarea[], WeakMap<OperationalAttributeField, ReadonlyMap<string, SuerteExceptionGeometry>>>>();

/**
 * Geometric split per Suerte, the only expensive step (polygon clipping). Memoized by (active model, subareas
 * list, partial field): selection, pan, zoom and restyling reuse it; it is rebuilt only when one of them changes.
 * Overlaps between subareas of one parent are blocked when they are created; any residual overlap is assigned
 * to the earlier subarea so no area is counted twice.
 */
export function suerteExceptionGeometry(model: LayerModel, subareas: readonly OperationalSubarea[], field: OperationalAttributeField): ReadonlyMap<string, SuerteExceptionGeometry> {
  const cached = geometryCache.get(model)?.get(subareas)?.get(field);
  if (cached) return cached;
  const bySuerte = new Map<string, OperationalSubarea[]>();
  subareas.forEach((subarea) => {
    if (subarea.parentLevel !== 'suerte' || !model.suertes.has(subarea.parentId) || !hasThematicValue(field.valuesByEntityId[subarea.id])) return;
    bySuerte.set(subarea.parentId, [...(bySuerte.get(subarea.parentId) ?? []), subarea]);
  });
  const result = new Map<string, SuerteExceptionGeometry>();
  bySuerte.forEach((items, suerteId) => {
    let parent: Polygon | MultiPolygon;
    try { parent = logicalParentGeometry(model, { level: 'suerte', id: suerteId }); } catch { return; }
    const parentCoordinates = asClipping(parent);
    const taken: Geom[] = [];
    const overrides: SuerteExceptionGeometry['overrides'][number][] = [];
    items.forEach((subarea) => {
      const geometry = subarea.feature.getGeometry();
      if (!(geometry instanceof Polygon || geometry instanceof MultiPolygon)) return;
      const inside = intersection(asClipping(geometry), parentCoordinates);
      const piece = significant(taken.length ? difference(inside, ...taken) : inside);
      if (!piece) return;
      taken.push(piece.coordinates);
      overrides.push({ subareaId: subarea.id, category: display(field.valuesByEntityId[subarea.id]), geometry: piece.geometry, areaM2: measureArea(piece.geometry).areaM2 });
    });
    if (!overrides.length) return;
    result.set(suerteId, { overrides, remnant: significant(difference(parentCoordinates, ...taken))?.geometry ?? null });
  });
  const byModel = geometryCache.get(model) ?? new WeakMap<readonly OperationalSubarea[], WeakMap<OperationalAttributeField, ReadonlyMap<string, SuerteExceptionGeometry>>>();
  const byList = byModel.get(subareas) ?? new WeakMap<OperationalAttributeField, ReadonlyMap<string, SuerteExceptionGeometry>>();
  byList.set(field, result); byModel.set(subareas, byList); geometryCache.set(model, byModel);
  return result;
}

const fragmentArea = (model: LayerModel, fragmentIds: readonly string[]): number => fragmentIds.reduce((sum, id) => {
  const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
  return sum + (geometry?.getType().includes('Polygon') ? measureArea(geometry).hectares : 0);
}, 0);

export interface MixedClassificationInput {
  readonly model: LayerModel;
  readonly baseField: string;
  readonly partialField: OperationalAttributeField;
  /** Visible theme target (DisplaySet ∩ ThemeTarget), as for the normal classification. Only these Suertes are composed. */
  readonly targetIds: ReadonlySet<string>;
  /** DisplaySet of the Maestro (guard: a target fragment that is not visible is never classified). */
  readonly displayIds: ReadonlySet<string>;
  readonly subareas: readonly OperationalSubarea[];
  readonly fields: readonly OperationalAttributeField[];
  /** Split table read by the principal classification (V1.8.1), so the base remnant shows the same category as elsewhere. */
  readonly split?: Pick<ClassificationConfig, 'field' | 'splitPart' | 'categorySplit'>;
}

let lastAssignments: { readonly key: readonly unknown[]; readonly result: MixedClassificationAssignments } | null = null;

/**
 * Category assignments of the mixed view. VISIBLE ∩ THEME TARGET decides which Suertes are classified (same rows as
 * masterClassificationAssignments); only then do their own Áreas parciales act as internal exceptions. Areas come from the final non-overlapping pieces (their sum equals the
 * visible active area of every Suerte); counts are unique Suertes per category, so a split Suerte counts once in
 * each category it shows. Fragments, MultiPolygon parts and subareas are never counted as Suertes.
 */
export function mixedClassificationAssignments(input: MixedClassificationInput): MixedClassificationAssignments {
  const key = [input.model, input.baseField, input.partialField, input.targetIds, input.displayIds, input.subareas, input.fields, input.split?.categorySplit, input.split?.splitPart];
  if (lastAssignments && lastAssignments.key.every((value, index) => value === key[index])) return lastAssignments.result;
  const { model, baseField, fields } = input;
  const classified = new Set([...input.targetIds].filter((id) => input.displayIds.has(id)));
  const split = suerteExceptionGeometry(model, input.subareas, input.partialField);
  const categoryByEntityId = new Map<string, string>(); const categoryByFragmentId = new Map<string, string>();
  const hasValueByEntityId = new Map<string, boolean>(); const hasValueByFragmentId = new Map<string, boolean>();
  const counts = new Map<string, number>(); const areaHa = new Map<string, number>();
  const piecesBySuerteId = new Map<string, readonly MixedThematicPiece[]>(); const subsetFragmentIds = new Set<string>();
  const add = (category: string, hectares: number): void => { areaHa.set(category, (areaHa.get(category) ?? 0) + hectares); };
  entityViewEntities(model, 'suerte', classified).forEach((row) => {
    const exceptions = split.get(row.id);
    const source = fieldValueForMasterEntity(model, baseField, row.fragmentIds, row.ids, fields);
    const raw = input.split && input.split.field === baseField ? principalValue(source, input.split) : source;
    const base = display(raw); const hasBase = hasThematicValue(raw);
    const rowArea = fragmentArea(model, row.fragmentIds);
    let pieces: MixedThematicPiece[] = [];
    if (exceptions) {
      pieces = exceptions.overrides.map((item) => ({ parentSuerteId: row.id, source: 'subarea' as const, sourceId: item.subareaId, category: item.category, hasValue: true, geometry: item.geometry, areaHa: item.areaM2 / 10_000 }));
      const overrideHa = pieces.reduce((sum, piece) => sum + piece.areaHa, 0);
      // The Suerte area is the sum of its active fragments, as everywhere else; the remnant takes the difference
      // so the pieces add up exactly. Fully covered: the numerical residue goes to the largest exception.
      if (exceptions.remnant) pieces.push({ parentSuerteId: row.id, source: 'base', sourceId: row.id, category: base, hasValue: hasBase, geometry: exceptions.remnant, areaHa: Math.max(0, rowArea - overrideHa) });
      else {
        const largest = pieces.reduce((best, piece, index) => piece.areaHa > pieces[best]!.areaHa ? index : best, 0);
        pieces[largest] = { ...pieces[largest]!, areaHa: Math.max(0, pieces[largest]!.areaHa + rowArea - overrideHa) };
      }
      piecesBySuerteId.set(row.id, pieces);
    }
    const entityHasValue = pieces.length ? pieces.some((piece) => piece.hasValue) : hasBase;
    categoryByEntityId.set(row.id, base); hasValueByEntityId.set(row.id, entityHasValue);
    row.fragmentIds.forEach((id) => { categoryByFragmentId.set(id, base); hasValueByFragmentId.set(id, entityHasValue); subsetFragmentIds.add(id); });
    if (!pieces.length) { counts.set(base, (counts.get(base) ?? 0) + 1); add(base, rowArea); return; }
    pieces.forEach((piece) => add(piece.category, piece.areaHa));
    new Set(pieces.map((piece) => piece.category)).forEach((category) => counts.set(category, (counts.get(category) ?? 0) + 1));
  });
  const result = { categoryByEntityId, categoryByFragmentId, hasValueByEntityId, hasValueByFragmentId, counts, areaHa, piecesBySuerteId, subsetFragmentIds };
  lastAssignments = { key, result };
  return result;
}
